import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import { Checkbox } from "carbon-react/esm/components/checkbox";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import Textbox from "carbon-react/esm/components/textbox";

import {
  type AccountingSettingsDocument,
  type AccountingSettingsResponse,
  type DimensionType,
  getAccountingSettings,
  journalPurposes,
  saveAccountingSettings,
} from "../api/accountingSettingsApi";
import { ApplicationApiError } from "../api/applicationApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { Option, SearchableSelect as Select } from "../components/SearchableSelect";

const standardDimensions: Array<[DimensionType, string]> = [
  ["entity", "Entity"],
  ["location", "Location"],
  ["department", "Department"],
  ["class", "Class"],
  ["project", "Project"],
  ["vendor", "Vendor"],
  ["customer", "Customer"],
  ["employee", "Employee"],
  ["item", "Item"],
];

export function AccountingSettingsPage() {
  const { api } = useContext(EmbeddedSessionContext);
  const [response, setResponse] = useState<AccountingSettingsResponse | null>(null);
  const [draft, setDraft] = useState<AccountingSettingsDocument | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "saving" | "error" | "conflict">("loading");
  const [confirmDirectPosting, setConfirmDirectPosting] = useState(false);

  useEffect(() => {
    if (!api) return;
    let active = true;
    void getAccountingSettings(api)
      .then((value) => {
        if (!active) return;
        const settings = withStandardDimensions(value.settings);
        setResponse({ ...value, settings });
        setDraft(structuredClone(settings));
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => { active = false; };
  }, [api]);

  if (status === "loading" || !draft || !response) {
    return <Loader loaderLabel="Loading accounting settings" size="medium" />;
  }
  if (status === "error") {
    return <Message open title="Accounting settings could not be loaded" variant="error">Reopen Loan Management from Sage Intacct.</Message>;
  }

  const save = async () => {
    if (!api) return;
    setConfirmDirectPosting(false);
    setStatus("saving");
    try {
      const saved = await saveAccountingSettings(api, draft);
      const settings = withStandardDimensions(saved.settings);
      setResponse({ ...saved, settings });
      setDraft(structuredClone(settings));
      setStatus("ready");
    } catch (error) {
      setStatus(error instanceof ApplicationApiError && error.status === 409 ? "conflict" : "error");
    }
  };

  const requestSave = () => {
    if (draft.postingRules.directPostingEnabled && !response.settings.postingRules.directPostingEnabled) {
      setConfirmDirectPosting(true);
    } else {
      void save();
    }
  };

  const optionsFor = (recordType: string, selectedId: string | null, dimensionKey?: string) => response.options.filter(
    (option) => option.recordType === recordType &&
      (recordType !== "user_defined" || option.dimensionKey === dimensionKey) &&
      (option.status === "active" || option.id === selectedId),
  );

  return (
    <div className="settings-stack">
      {status === "conflict" && (
        <Message open title="Settings changed" variant="warning">Reload this page before saving your changes.</Message>
      )}
      {response.options.length === 0 && (
        <Message open title="Sage master data is not available" variant="info">
          Run Sync all master data from Sage connection, then return here to configure journals, accounts, and dimensions.
        </Message>
      )}

      <section className="settings-section" aria-labelledby="journals-heading">
        <h2 id="journals-heading">Journals</h2>
        <div className="settings-grid">
          {journalPurposes.map(([purpose, label]) => (
            <MappingSelect
              key={purpose}
              id={`journal-${purpose}`}
              label={label}
              onChange={(value) => setDraft({ ...draft, journals: { ...draft.journals, [purpose]: value } })}
              options={optionsFor("journal", draft.journals[purpose])}
              value={draft.journals[purpose]}
            />
          ))}
        </div>
      </section>

      <section className="settings-section" aria-labelledby="posting-heading">
        <h2 id="posting-heading">Posting behavior</h2>
        <div className="settings-grid">
          <Select
            id="batch-grouping-mode"
            label="Batch grouping"
            onChange={(event) => setDraft({
              ...draft,
              postingRules: {
                ...draft.postingRules,
                batchGroupingMode: event.target.value as AccountingSettingsDocument["postingRules"]["batchGroupingMode"],
              },
            })}
            value={draft.postingRules.batchGroupingMode}
          >
            <Option text="Per loan" value="per_loan" />
            <Option text="Per entity and date" value="per_entity_date" />
            <Option text="Consolidated" value="consolidated" />
          </Select>
          <Select
            id="closed-period-behavior"
            label="Closed period behavior"
            onChange={(event) => setDraft({
              ...draft,
              postingRules: {
                ...draft.postingRules,
                closedPeriodBehavior: event.target.value as AccountingSettingsDocument["postingRules"]["closedPeriodBehavior"],
              },
            })}
            value={draft.postingRules.closedPeriodBehavior}
          >
            <Option text="Block posting" value="block" />
            <Option text="Use next open period" value="next_open_period" />
          </Select>
          <Textbox
            label="Posting description"
            maxLength={500}
            onChange={(event) => setDraft({
              ...draft,
              postingRules: { ...draft.postingRules, postingDescriptionTemplate: event.target.value },
            })}
            value={draft.postingRules.postingDescriptionTemplate}
          />
        </div>
        <div className="settings-toggles">
          <Checkbox
            checked={draft.postingRules.draftEntryByDefault}
            label="Create draft entries by default"
            onChange={(event) => setDraft({
              ...draft,
              postingRules: { ...draft.postingRules, draftEntryByDefault: event.target.checked },
            })}
          />
          <Checkbox
            checked={draft.postingRules.attachPostingSupport}
            label="Attach posting support"
            onChange={(event) => setDraft({
              ...draft,
              postingRules: { ...draft.postingRules, attachPostingSupport: event.target.checked },
            })}
          />
          <Checkbox
            checked={draft.postingRules.directPostingEnabled}
            label="Enable direct posting"
            onChange={(event) => setDraft({
              ...draft,
              postingRules: { ...draft.postingRules, directPostingEnabled: event.target.checked },
            })}
          />
        </div>
      </section>

      <div className="settings-actions">
        <Button
          disabled={status === "saving" || JSON.stringify(draft) === JSON.stringify(response.settings)}
          onClick={requestSave}
          type="button"
          variant="default"
          variantType="primary"
        >
          {status === "saving" ? "Saving" : "Save settings"}
        </Button>
      </div>

      <ConfirmationDialog
        confirmLabel="Enable and save"
        isConfirming={status === "saving"}
        onCancel={() => setConfirmDirectPosting(false)}
        onConfirm={() => void save()}
        open={confirmDirectPosting}
        title="Enable direct posting?"
      >
        Direct posting allows future eligible accounting events to be sent without remaining as drafts. This does not post any existing event.
      </ConfirmationDialog>
    </div>
  );
}

function MappingSelect({
  id,
  label,
  onChange,
  options,
  value,
}: {
  id: string;
  label: string;
  onChange(value: string | null): void;
  options: AccountingSettingsResponse["options"];
  value: string | null;
}) {
  const selected = options.find((option) => option.id === value);
  return (
    <div>
      <Select
        id={id}
        label={label}
        onChange={(event) => onChange(event.target.value || null)}
        value={value ?? ""}
      >
        <Option text="Not configured" value="" />
        {options.map((option) => (
          <Option
            key={option.id}
            text={`${option.displayName} (${option.recordNo})${option.status === "inactive" ? " - inactive" : ""}`}
            value={option.id}
          />
        ))}
      </Select>
      {selected?.status === "inactive" && <p className="field-warning">The saved Sage record is inactive. Select an active record before saving.</p>}
    </div>
  );
}

function withStandardDimensions(settings: AccountingSettingsDocument): AccountingSettingsDocument {
  const dimensions = [...settings.dimensions];
  for (const [dimensionType, displayName] of standardDimensions) {
    if (!dimensions.some(({ dimensionKey }) => dimensionKey === dimensionType)) {
      dimensions.push({ dimensionKey: dimensionType, dimensionType, displayName, masterRecordId: null });
    }
  }
  return { ...settings, dimensions };
}
